import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Segmented,
  Space,
  Typography,
  Divider,
  Tag,
  Button,
  Tooltip,
  Alert,
  AutoComplete,
  Select,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
  EditOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import CatalogoItemPicker from 'components/admin/Hallazgos/CatalogoItemPicker';
import MonedaBadge from 'components/admin/Hallazgos/MonedaBadge';
import SaveItemToCatalogModal from 'components/admin/Budget/SaveItemToCatalogModal';
import AscensorInlineSelect from 'components/selects/AscensorInlineSelect';
import { getTipoCambioApi } from 'api/hallazgoFlujo';
import { useTenantColors } from 'hooks/useTenantColors';
import useTenantFlags from 'hooks/useTenantFlags';

// Body compartido dentro de BudgetEditorDrawer (create + edit). El padre aporta
// el `<Form>` + submit; este componente renderiza moneda + campos de texto +
// items + subtotal, y notifica cambios de moneda/tipo de cambio vía callback
// para que el padre los incluya en el payload.

// UF y USD ocultos: la conversión por tipo de cambio no está operativa. Se deja
// solo CLP hasta que el flujo multi-moneda se habilite de punta a punta.
const MONEDAS = [{ value: 'CLP', label: 'CLP' }];

// IVA Chile (19%) para el preview del total de los items.
const IVA_RATE = 0.19;

// Opciones curadas para `Tiempo de producción`. El campo es free-text
// (VARCHAR(120)) y el 95% de los budgets historicos tienen valor, pero con
// mucho ruido: trailing spaces, "1", "10 hábiles", "asd", etc. El PDF lo
// renderiza como linea de prosa en las condiciones ("Tiempo de produccion:
// {finishTime}."), por eso las sugerencias son frases comerciales completas
// que calzan bien en el documento. El usuario puede escribir lo que quiera.
const FINISH_TIME_SUGGESTIONS = [
  '5 días hábiles desde la aprobación del presupuesto',
  '10 días hábiles desde la aprobación del presupuesto',
  '15 días hábiles desde la aprobación del presupuesto',
  '3 días hábiles por ascensor desde la aprobación',
  '2 semanas desde la aprobación del presupuesto',
  'Entrega inmediata (24 horas hábiles)',
  'Sujeto a disponibilidad de repuestos',
  'A convenir según complejidad de la obra',
].map((v) => ({ value: v }));

const fmtDate = (d) => {
  if (!d) return null;
  try {
    const x = new Date(d);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
};

function BudgetEditor({
  form,
  initialMoneda = 'CLP',
  onMonedaChange,
  showCatalogoPicker = false,
  hideTextFields = false,
  idClient,
  // BUG 3: hallazgos vinculados al presupuesto. Si vienen, cada ítem puede
  // declarar cuál resuelve (selector por fila); ese id_hallazgo se persiste en
  // budgetitem y se propaga a order_item al generar la ODT. Si está vacío
  // (presupuesto standalone sin hallazgos) no se renderiza el selector.
  hallazgos = [],
  // Catálogo de garantías del tenant (Task 8: GET /garantias), cargado por
  // BudgetEditorDrawer. Alimenta el selector "Tipo de garantía".
  garantias = [],
  // C3 (review 2026-07-13): los presupuestos v1/v2 se imprimen con
  // renderizadores viejos que solo conocen el texto libre `guarantee` — no
  // saben leer `guarantee_text` (el snapshot del catálogo). Mostrar el
  // desplegable en un presupuesto v1/v2 permitiría elegir una garantía que
  // el PDF nunca imprime. Por default en true: un presupuesto nuevo (sin
  // `budget`, ver BudgetEditorDrawer) siempre nace en v3.
  showCatalogoGarantia = true,
  // Bloqueante 1 (verificador feature garantías, 2026-07-13): con el
  // presupuesto en estado 'aprobado' la cláusula queda congelada en el
  // backend — deshabilita el catálogo para que no se pueda "editar y
  // guardar" un cambio que el servidor va a descartar en silencio.
  guaranteeLocked = false,
}) {
  const [moneda, setMoneda] = useState(initialMoneda);
  const [tipoCambio, setTipoCambio] = useState(null);
  const [tcLoading, setTcLoading] = useState(false);
  const [promoteState, setPromoteState] = useState({
    open: false,
    fieldName: null,
    snapshot: null,
  });

  const { secondary } = useTenantColors();
  const { readOnly } = useTenantFlags();

  const openPromoteModal = (fieldName) => {
    const it = form.getFieldValue(['items', fieldName]) || {};
    setPromoteState({
      open: true,
      fieldName,
      snapshot: {
        nombre: it.description || '',
        descripcion: '',
        precio: it.unitValue ?? null,
        moneda,
      },
    });
  };

  const closePromoteModal = () => setPromoteState({ open: false, fieldName: null, snapshot: null });

  const handleItemPromoted = (item) => {
    if (promoteState.fieldName == null) return;
    form.setFields([
      { name: ['items', promoteState.fieldName, 'id_coleccion_item'], value: item.id },
      { name: ['items', promoteState.fieldName, 'description'], value: item.nombre },
    ]);
    message.success('Item agregado al catálogo');
  };

  useEffect(() => {
    setMoneda(initialMoneda || 'CLP');
  }, [initialMoneda]);

  useEffect(() => {
    if (moneda === 'CLP') {
      setTipoCambio(null);
      onMonedaChange?.({ moneda, tipoCambio: null });
      return;
    }
    setTcLoading(true);
    getTipoCambioApi(moneda)
      .then((res) => {
        const next =
          res && typeof res.valor === 'number'
            ? { valor: res.valor, fecha: res.fecha, source: res.source }
            : null;
        setTipoCambio(next);
        onMonedaChange?.({ moneda, tipoCambio: next });
      })
      .catch(() => {
        setTipoCambio(null);
        onMonedaChange?.({ moneda, tipoCambio: null });
      })
      .finally(() => setTcLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moneda]);

  const items = Form.useWatch('items', form) || [];

  // BUG 3: opciones del selector de hallazgo por ítem + hallazgos sin cubrir
  // (ninguna fila los referencia). Espeja el aviso "sin tarea asignada" del
  // GenerateOrderDrawer para que el usuario sepa qué hallazgos quedan sueltos.
  const hallazgoOpts = useMemo(
    () =>
      (hallazgos || []).map((h) => ({
        value: h.id,
        label: `#${h.id} · ${String(h.descripcion ?? '').slice(0, 60)}${
          String(h.descripcion ?? '').length > 60 ? '…' : ''
        }`,
      })),
    [hallazgos]
  );
  const uncoveredHallazgos = useMemo(() => {
    if (!hallazgos || hallazgos.length === 0) return [];
    const covered = new Set(items.map((it) => it?.id_hallazgo).filter((v) => v != null));
    return hallazgos.filter((h) => !covered.has(h.id));
  }, [items, hallazgos]);

  const subtotal = useMemo(
    () =>
      items.reduce((acc, it) => acc + Number(it?.unitValue || 0) * Number(it?.quantity || 0), 0),
    [items]
  );
  const subtotalEnCLP = useMemo(() => {
    if (moneda === 'CLP' || !tipoCambio) return null;
    return subtotal * tipoCambio.valor;
  }, [moneda, tipoCambio, subtotal]);
  const ivaAmount = useMemo(() => subtotal * IVA_RATE, [subtotal]);
  const totalConIva = useMemo(() => subtotal * (1 + IVA_RATE), [subtotal]);

  // Descuento: fracción 0..1 que vive en el form del drawer (0.2 = 20%). Solo
  // editable en modo edición; en creación es 0. Se muestra el total con y sin
  // descuento para comparar de un vistazo.
  const discountRaw = Form.useWatch('discount', form);
  const discount = Number(discountRaw) || 0;
  const hasDiscount = discount > 0 && discount < 1;
  const descuentoAmount = useMemo(() => subtotal * discount, [subtotal, discount]);
  const netoConDescuento = useMemo(() => subtotal * (1 - discount), [subtotal, discount]);
  const ivaConDescuento = useMemo(() => netoConDescuento * IVA_RATE, [netoConDescuento]);
  const totalConDescuento = useMemo(() => netoConDescuento * (1 + IVA_RATE), [netoConDescuento]);

  return (
    <>
      {/* ====== Bloque moneda ====== */}
      <div style={{ marginBottom: 20 }}>
        <Typography.Text
          strong
          style={{
            fontSize: 11,
            letterSpacing: 0.8,
            color: '#64748b',
            display: 'block',
            marginBottom: 8,
          }}
        >
          MONEDA DEL PRESUPUESTO
        </Typography.Text>
        <Space size="middle" align="center" wrap>
          <Segmented options={MONEDAS} value={moneda} onChange={setMoneda} />
          {tcLoading ? (
            <Tag color="default">consultando tipo de cambio…</Tag>
          ) : tipoCambio ? (
            <Tooltip
              title={
                tipoCambio.source === 'fallback'
                  ? 'Valor tomado del fallback configurado (API externa no responde)'
                  : tipoCambio.source === 'cache'
                  ? 'Valor desde cache (< 24h)'
                  : 'Valor consultado en vivo a mindicador.cl'
              }
            >
              <Tag
                color={tipoCambio.source === 'fallback' ? 'warning' : 'processing'}
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                1 {moneda} ={' '}
                {Number(tipoCambio.valor).toLocaleString('es-CL', {
                  maximumFractionDigits: 4,
                })}{' '}
                CLP · {fmtDate(tipoCambio.fecha)}
              </Tag>
            </Tooltip>
          ) : moneda !== 'CLP' ? (
            <Tag color="error">tipo de cambio no disponible</Tag>
          ) : null}
        </Space>
      </div>

      {/* ====== Campos de texto comunes ====== */}
      {hideTextFields ? null : (
        <>
          <Form.Item name="reason" label="Motivo / título corto">
            <Input placeholder="p.ej. Reparación motor tracción" maxLength={90} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {showCatalogoGarantia ? (
              <>
                <Form.Item
                  name="guaranteeId"
                  label="Tipo de garantía"
                  help={
                    guaranteeLocked
                      ? 'La garantía quedó fija al aprobarse el presupuesto: es el texto que el cliente aceptó y ya no se puede cambiar.'
                      : undefined
                  }
                >
                  <Select
                    allowClear
                    disabled={guaranteeLocked}
                    placeholder="Sin garantía"
                    options={garantias.map((g) => ({ value: g.id, label: g.nombre }))}
                  />
                </Form.Item>
                <Form.Item
                  name="guaranteeMonths"
                  label="Meses de garantía"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value) =>
                        getFieldValue('guaranteeId') && !value
                          ? Promise.reject(new Error('Indique cuántos meses cubre la garantía'))
                          : Promise.resolve(),
                    }),
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={120}
                    precision={0}
                    disabled={guaranteeLocked}
                    style={{ width: '100%' }}
                    placeholder="6"
                  />
                </Form.Item>
              </>
            ) : null}
            <Form.Item
              name="guarantee"
              label="Garantía"
              style={showCatalogoGarantia ? undefined : { gridColumn: '1 / -1' }}
              extra={
                showCatalogoGarantia
                  ? 'Si elige un tipo de garantía del catálogo, esa cláusula es la que se imprime en el documento. Este texto queda como respaldo.'
                  : undefined
              }
            >
              <Input placeholder="6 meses, 1 año…" />
            </Form.Item>
            <Form.Item name="finishTime" label="Tiempo de producción">
              <AutoComplete
                options={FINISH_TIME_SUGGESTIONS}
                placeholder="p.ej. 5 días hábiles"
                allowClear
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>
          <Form.Item name="description" label="Descripción general (opcional)">
            <Input.TextArea rows={2} placeholder="Contexto del presupuesto — alcance, supuestos…" />
          </Form.Item>
          <Form.Item name="conditions" label="Condiciones comerciales (opcional)">
            <Input.TextArea rows={2} placeholder="Forma de pago, tiempo de entrega…" />
          </Form.Item>
        </>
      )}

      {/* ====== Items ====== */}
      <Divider style={{ margin: '20px 0 12px', borderColor: '#e2e6eb' }}>
        <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
          ITEMS DEL PRESUPUESTO
        </Typography.Text>
      </Divider>

      <Form.List name="items">
        {(fields, { add, remove }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fields.map((field, idx) => {
              const linkedId = form.getFieldValue(['items', field.name, 'id_coleccion_item']);
              const isLinked = Boolean(linkedId);
              return (
                <div
                  key={field.key}
                  style={{
                    padding: '10px 12px',
                    background: '#f6f7f9',
                    border: '1px solid #e2e6eb',
                    borderLeft: `3px solid ${isLinked ? secondary : '#cbd2d9'}`,
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          color: '#94a3b8',
                          fontSize: 12,
                        }}
                      >
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      {showCatalogoPicker && isLinked ? (
                        <Tag
                          color="warning"
                          icon={<LinkOutlined />}
                          style={{ marginInlineEnd: 0, fontSize: 10 }}
                        >
                          catálogo
                        </Tag>
                      ) : showCatalogoPicker ? (
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            fontSize: 10,
                            background: 'transparent',
                            borderStyle: 'dashed',
                          }}
                          icon={<EditOutlined />}
                        >
                          item libre
                        </Tag>
                      ) : null}
                      {showCatalogoPicker && !isLinked && !readOnly ? (
                        <Tooltip title="Guardar este item en el catálogo para reutilizarlo después">
                          <Button
                            type="link"
                            size="small"
                            icon={<DatabaseOutlined />}
                            style={{ padding: '0 4px', fontSize: 11 }}
                            onClick={() => openPromoteModal(field.name)}
                          >
                            guardar al catálogo
                          </Button>
                        </Tooltip>
                      ) : null}
                    </div>
                    {!readOnly && (
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => remove(field.name)}
                        aria-label="Quitar item"
                      />
                    )}
                  </div>

                  {showCatalogoPicker ? (
                    <CatalogoItemPicker
                      value={linkedId || undefined}
                      moneda={moneda}
                      size="small"
                      placeholder="Buscar en catálogo (opcional)…"
                      onChange={(item) => {
                        if (!item) return;
                        form.setFields([
                          { name: ['items', field.name, 'id_coleccion_item'], value: item.id },
                          { name: ['items', field.name, 'description'], value: item.nombre },
                          {
                            name: ['items', field.name, 'unitValue'],
                            value: item.precio != null ? Number(item.precio) : 0,
                          },
                        ]);
                      }}
                      onClear={() => {
                        form.setFields([
                          { name: ['items', field.name, 'id_coleccion_item'], value: null },
                        ]);
                      }}
                    />
                  ) : null}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '110px 1fr 90px 120px',
                      gap: 8,
                    }}
                  >
                    <Form.Item
                      {...field}
                      key={`${field.key}-elevator`}
                      name={[field.name, 'elevator']}
                      style={{ marginBottom: 0 }}
                      extra={
                        Array.isArray(items[idx]?.elevator) && items[idx].elevator.length > 1 ? (
                          <span style={{ color: secondary, fontSize: 10 }}>
                            Se generarán {items[idx].elevator.length} tareas al crear la ODT
                          </span>
                        ) : null
                      }
                    >
                      <AscensorInlineSelect
                        mode="multiple"
                        idClient={idClient}
                        size="small"
                        placeholder="Ascensor / ubicación"
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      key={`${field.key}-description`}
                      name={[field.name, 'description']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Descripción requerida' }]}
                    >
                      <Input placeholder="Descripción del trabajo / repuesto" size="small" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      key={`${field.key}-quantity`}
                      name={[field.name, 'quantity']}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0}
                        placeholder="Cant."
                        size="small"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      key={`${field.key}-unitValue`}
                      name={[field.name, 'unitValue']}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0}
                        placeholder="Precio unit."
                        size="small"
                        style={{ width: '100%' }}
                        formatter={(v) => `${v ?? ''}`}
                      />
                    </Form.Item>
                  </div>
                  {hallazgos.length > 0 ? (
                    <Form.Item
                      {...field}
                      key={`${field.key}-id_hallazgo`}
                      name={[field.name, 'id_hallazgo']}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        allowClear
                        showSearch
                        size="small"
                        disabled={readOnly}
                        // Hook estable para el E2E (e2e/bug3HallazgoBudgetOdt.spec.ts):
                        // ubica el selector de hallazgo por ítem sin depender del placeholder.
                        className="e2e-hallazgo-item-select"
                        placeholder={
                          <span>
                            <LinkOutlined style={{ marginRight: 6 }} />
                            Hallazgo que resuelve (opcional)
                          </span>
                        }
                        optionFilterProp="label"
                        options={hallazgoOpts}
                      />
                    </Form.Item>
                  ) : null}
                  <Form.Item
                    {...field}
                    key={`${field.key}-id_coleccion_item`}
                    name={[field.name, 'id_coleccion_item']}
                    hidden
                  >
                    <Input />
                  </Form.Item>
                </div>
              );
            })}
            {!readOnly && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({ elevator: [], description: '', quantity: 1, unitValue: 0 })}
                block
              >
                Agregar item
              </Button>
            )}
          </div>
        )}
      </Form.List>

      {/* ====== BUG 3: hallazgos sin cubrir ====== */}
      {uncoveredHallazgos.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={`${uncoveredHallazgos.length} hallazgo(s) sin ítem asignado`}
          description={
            <div>
              Estos hallazgos quedan vinculados al presupuesto pero ningún ítem los resuelve. Al
              generar la ODT no se cerrarán solos hasta que una tarea los tome. Asigná un ítem o
              dejalos para una segunda vuelta.
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {uncoveredHallazgos.map((h) => (
                  <Tag key={h.id} color="warning" style={{ marginInlineEnd: 0 }}>
                    #{h.id} · {String(h.descripcion ?? '').slice(0, 40)}
                  </Tag>
                ))}
              </div>
            </div>
          }
        />
      ) : null}

      {/* ====== Subtotal / IVA / Total ====== */}
      <div
        style={{
          marginTop: 20,
          padding: 16,
          background: '#0f172a',
          color: '#fff',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text style={{ color: '#94a3b8', fontSize: 11, letterSpacing: 0.8 }}>
            NETO
          </Typography.Text>
          <MonedaBadge
            amount={subtotal}
            moneda={moneda}
            convertedTo={subtotalEnCLP != null ? 'CLP' : undefined}
            convertedAmount={subtotalEnCLP}
            rate={tipoCambio?.valor}
            rateDate={tipoCambio?.fecha}
            size="md"
            theme="dark"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text style={{ color: '#94a3b8', fontSize: 11, letterSpacing: 0.8 }}>
            IVA (19%)
          </Typography.Text>
          <MonedaBadge amount={ivaAmount} moneda={moneda} size="md" theme="dark" />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid #334155',
            paddingTop: 8,
          }}
        >
          <Typography.Text style={{ color: '#e2e8f0', fontSize: 12, letterSpacing: 0.8 }}>
            {hasDiscount ? 'TOTAL CON IVA (sin desc.)' : 'TOTAL CON IVA'}
          </Typography.Text>
          <MonedaBadge amount={totalConIva} moneda={moneda} size="lg" theme="dark" />
        </div>

        {/* Bloque con descuento — solo si hay un descuento aplicado (edición). */}
        {hasDiscount ? (
          <>
            <Divider style={{ borderColor: '#334155', margin: '4px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text style={{ color: '#fbbf24', fontSize: 11, letterSpacing: 0.8 }}>
                DESCUENTO ({Math.round(discount * 100)}%)
              </Typography.Text>
              <MonedaBadge amount={-descuentoAmount} moneda={moneda} size="md" theme="dark" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text style={{ color: '#94a3b8', fontSize: 11, letterSpacing: 0.8 }}>
                NETO CON DESCUENTO
              </Typography.Text>
              <MonedaBadge amount={netoConDescuento} moneda={moneda} size="md" theme="dark" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text style={{ color: '#94a3b8', fontSize: 11, letterSpacing: 0.8 }}>
                IVA (19%)
              </Typography.Text>
              <MonedaBadge amount={ivaConDescuento} moneda={moneda} size="md" theme="dark" />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid #334155',
                paddingTop: 8,
              }}
            >
              <Typography.Text style={{ color: '#fff', fontSize: 12, letterSpacing: 0.8 }}>
                TOTAL CON IVA (con desc.)
              </Typography.Text>
              <MonedaBadge amount={totalConDescuento} moneda={moneda} size="lg" theme="dark" />
            </div>
          </>
        ) : null}
      </div>

      {moneda !== 'CLP' && !tipoCambio && !tcLoading ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="No se pudo obtener el tipo de cambio"
          description="El presupuesto se creará de todos modos si lo envías; el backend rechazará si no hay fallback configurado. Configurá TIPO_CAMBIO_FALLBACK_UF / TIPO_CAMBIO_FALLBACK_USD si la API externa está caída."
        />
      ) : null}

      <SaveItemToCatalogModal
        open={promoteState.open}
        snapshot={promoteState.snapshot}
        onClose={closePromoteModal}
        onSaved={handleItemPromoted}
      />
    </>
  );
}

export default BudgetEditor;
